(ns common.schema.entities.workspace.kyc.templates.demo.legal-entity
  (:require
   [common.schema.entities.workspace.kyc.templates.demo.company-config :as company-config]
   [common.schema.entities.workspace.kyc.templates.demo.pep :as pep]
   [common.schema.entities.workspace.kyc.templates.demo.ubo :as ubo]
   [common.schema.entities.workspace.kyc.templates.demo.board-member :as board-member]
   [common.schema.entities.workspace.kyc.questions.info-texts :as info-texts]
   [common.schema.entities.workspace.kyc.questions.questions-registry :as questions-registry]))

(def schema
  {:kyc-template/template-name
   "Legal entity"

   :kyc-template/kyc-form-preface
   company-config/form-preface-text

   :kyc-template/svg-logo
   company-config/svg-logo


   :kyc-template/png-logo
   company-config/png-logo

   :kyc-template/help-email-address
   company-config/contact-email

   :kyc-template/kyc-form-title
   {:translations {:en "Know Your Customer (KYC) form"
                   :fi "Asiakastietokysely"}}

   :kyc.legal-entity/basic-information
   {:ord
    1.0
    :translations
    {:en "Basic information"
     :fi "Perustiedot"}
    :questions
    (questions-registry/resolve-questions
     [[:kyc-legal-entity/name]
      [:kyc-legal-entity/jurisdiction]
      [:kyc-legal-entity/business-id]
      [:kyc-legal-entity/trade-register-extract]
      [:kyc-legal-entity/legal-form]
      [:kyc-legal-entity/legal-form-other]
      [:kyc-legal-entity/actual-line-of-business]
      [:kyc-legal-entity/actual-line-of-business-other]])}

   :kyc.legal-entity/address-information
   {:ord
    2.0
    :translations
    {:en "Address"
     :fi "Osoite"}
    :questions
    (questions-registry/resolve-questions
     [[:kyc-legal-entity/address]
      [:kyc-legal-entity/zip-code]
      [:kyc-legal-entity/city]
      [:kyc-legal-entity/country]
      [:kyc-legal-entity/main-business-address]])}

   :kyc.legal-entity/financial-information
   {:ord
    3.0
    :translations
    {:en "Financial information"
     :fi "Pääoma"}
    :info
    info-texts/financials-info
    :questions
    (questions-registry/resolve-questions
     [[:kyc-legal-entity/financial-records-delivery]
      [:kyc-legal-entity/financial-records-url]
      [:kyc-legal-entity/attachment-financial-records]
      [:kyc-legal-entity/size-of-investment-portfolio]
      [:kyc-legal-entity/split-of-investment-portfolio]
      [:kyc-legal-entity/source-of-funds]
      [:kyc-legal-entity/source-of-funds-allocation-split]
      [:kyc-legal-entity/source-of-funds-comment]
      [:kyc-legal-entity/source-of-wealth]])}

   :kyc.legal-entity/ownership-structure-and-ubos
   {:ord
    4.0
    :translations
    {:en "Ownership structure"
     :fi "Omistusrakenne"}
    :info
    {:en
     (str
      "This section does not apply to companies that are listed on a stock exchange within the EEA "
      "(or outside the EEA if their obligations concerning provision of information are equivalent to "
      "the ones set out in MiFID 2004/39EC).")
     :fi
     (str
      "Tämä kohta ei koske sijoittajia, joiden osake on noteerattu ETA- valtion pörssissä "
      "(tai ETA:n ulkopuolella, jos listayhtiön tiedonantoa koskevat velvollisuudet vastaavat "
      "MiFID 2004/39EC -direktiivissä säänneltyjä velvollisuuksia ja soveltuvat sijoittajaan).")}
    :questions
    (questions-registry/resolve-questions
     [[:kyc-legal-entity/listed-in-stock-exchange?]
      [:kyc-legal-entity/organization-chart-delivery]
      [:kyc-legal-entity/attachment-organization-chart]
      [:kyc-legal-entity/ownership-chart-url]
      [:kyc-legal-entity/ownership-other]
      [:kyc-legal-entity/has-agreement-of-voting-rights?]
      [:kyc-legal-entity/shareholder-or-other-agreement-description]])}

   :kyc-legal-entity/board-member-data
   {:ord
    5.0
    :translations
    {:en "Board members"
     :fi "Hallituksen jäsenet"}
    :questions
    (questions-registry/resolve-questions
     [[:kyc-legal-entity/board-members-edn {:data/template board-member/schema}]])
    :full-width-section? true}

   :kyc-legal-entity/ubo-data
   {:ord
    6.0
    :translations
    {:en "Ultimate beneficial owners (UBO)"
     :fi "Yhteisön tosiasialliset edunsaajat"}
    :conditional-block
    (fn [answers _kyc-process]
      (not= :yes (:kyc-legal-entity/listed-in-stock-exchange? answers)))
    :questions
    (questions-registry/resolve-questions
     [[:kyc-legal-entity/ubo-edn {:data/template ubo/schema}]])}

   :kyc-legal-entity/PEP-data
   {:ord
    7.0
    :translations
    {:en "Politically exposed person (PEP)"
     :fi "Poliittinen vaikutusvalta (PEP)"}
    :info
    {:en
     (str
      "<p>This list automatically contains all UBO's who are marked as PEP. If the investor has other "
      "politically exposed persons, add them here</p>"
      "<p>Confirm for instance following positions:"
      "<ul><li>Member of the board</li>"
      "<li>Chief executive officer</li>"
      "<li>Representative (for example the signatories of this form)</li></ul></p>"
      "<p>A person is considered a PEP, who is in a prominent public function or an immediate family "
      "member or a close associate of such a person either in Finland or another country?</p>")

     :fi
     (str
      "<p>Lisää tähän poliittisesti vaikutusvaltaiset henkilöt joita et ole jo kirjannut edelliselle "
      "UBO listalle.</p>"
      "<p>Muista esimerkiksi henkilöt seuraavissa positioissa:"
      "<ul><li>Hallituksen jäsen</li>"
      "<li>Toimitusjohtaja</li>"
      "<li>Henkilöt, joilla on nimenkirjoitusoikeus (esimerkiksi tämän lomakkeen allekirjoittaja)</li></ul></p>"
      "<p>Henkilö on poliittisesti vaikutusvaltainen, jos hän tai hänen lähipiiriinsä kuuluva henkilö "
      "toimii tai on toiminut merkittävässä julkisessa virassa Suomessa tai toisessa valtiossa.</p>")}
    :questions
    (questions-registry/resolve-questions
     [[:kyc-legal-entity/has-peps?]
      [:kyc-legal-entity/pep-edn {:data/template pep/schema}]])}

   ; Removed as per ticket DEV-5104
   #_:kyc.legal-entity/investor-categorization
   #_{:ord
      8.0
      :translations
      {:en "Investor categorization"
       :fi "Sijoittaja"}

      :questions
      (questions-registry/resolve-questions
       [[:kyc-legal-entity/professional-investor?]
        [:kyc-legal-entity/professional-investor-criteria]
        [:kyc-legal-entity/large-undertaking-criteria]
        [:kyc-legal-entity/other-professional-investor-type]
        [:kyc-legal-entity/professional-investor-notes]])

      :info
      {:en "Info"
       :fi "Info"}}

   :kyc.legal-entity/tax-reporting-information
   {:ord
    8.0
    :translations
    {:en "Tax reporting"
     :fi "Verotustiedot"}
    :questions
    (questions-registry/resolve-questions
     [[:kyc-legal-entity/tax-countries]
      [:kyc-legal-entity/usa-tax-residency]
      [:kyc-legal-entity/usa-tax-residency-yes]
      [:kyc-legal-entity/usa-tax-residency-no]
      [:kyc-legal-entity/fatca-specified-us-person?]
      [:kyc-legal-entity/financial-institution?]
      [:kyc-legal-entity/fatca-classification]
      [:kyc-legal-entity/fatca-classification-other]
      [:kyc-legal-entity/have-GIIN?]
      [:kyc-legal-entity/fatca-GIIN]
      [:kyc-legal-entity/fatca-no-GIIN]
      [:kyc-legal-entity/fatca-no-GIIN-other]
      [:kyc-legal-entity/GIIN-Sponsor]
      [:kyc-legal-entity/GIIN-Sponsor-GIIN]
      [:kyc-legal-entity/non-financial-entity-classification]
      [:kyc-legal-entity/non-financial-entity-classification-other]])}

   :kyc-legal-entity/crs-classification
   {:ord
    9.0
    :translations
    {:en "CRS classification for all investors"
     :fi "CRS luokitus kaikille sijoittajille"}
    :questions
    (questions-registry/resolve-questions
     [[:kyc-legal-entity/crs-classification]
      [:kyc-legal-entity/crs-classification-other]
      [:kyc-legal-entity/crs-tax-reporting-info]])}

   :kyc-legal-entity/sanctions-exposure
   {:ord
    10.0
    :translations
    {:en "Sanctions Exposure"
     :fi "Pakotteiden kohteena oleminen"}
    :info
    {:en
     (str
      "<p>We adhere to United Nations (UN) and European Union (EU) sanctions on a group-wide basis and "
      "comply with United States (US) sanctions laws and regulations generally.</p>"
      "<p>The information captured within the questionnaire "
      "helps us to understand any potential exposure to Sanctions in order to manage our adherence to "
      "the above mentioned programs, laws and regulations.</p>")
     :fi
     (str
      "<p>Sitoudumme noudattamaan YK:n ja EU:n pakotteita konsernitasolla, sekä USA:n pakolakeja ja "
      "-sääntelyä yleisesti.</p>"
      "<p>Lomakkeella annetut tiedot auttavat meitä ymmärtämään mahdollista pakotteille altistumista, "
      "jotta voimme sitoutua edellä mainittujen lakien ja asetusten noudattamiseen.</p>")}
    :questions
    (questions-registry/resolve-questions
     [[:kyc-legal-entity/sactions-target]
      [:kyc-legal-entity/geographical-exposure]])}

   :kyc-legal-entity/additional-documents
   {:ord
    11.0

    :translations
    {:en "Additional documents"
     :fi "Liitetiedostot"}

    :keep-together?
    true

    :info
    {:en ""
     :fi ""}

    :questions
    (questions-registry/resolve-questions
     [[:kyc-legal-entity/number-of-documents]
      [:kyc-legal-entity/document-1]
      [:kyc-legal-entity/document-2]
      [:kyc-legal-entity/document-3]
      [:kyc-legal-entity/document-4]
      [:kyc-legal-entity/document-5]])}

   :kyc-legal-entity/confirmation
   {:ord
    12.0
    :translations
    {:en "Confirm entered data is correct"
     :fi "Syötettyjen tietojen vahvistaminen"}
    :questions
    (questions-registry/resolve-questions
     [[:kyc-legal-entity/confirmation-place]
      [:kyc-legal-entity/confirmation-date]
      [:kyc-legal-entity/confirmation-name]])}

   :kyc-legal-entity/identifications
   {:ord
    13.0

    :translations
    {:en "Signer identification"
     :fi "Allekirjoittajien tunnistetiedot"}

    :keep-together?
    true

    :info
    {:en ""
     :fi ""}

    :questions
    (questions-registry/resolve-questions
     [[:kyc-legal-entity/number-of-signers]
      [:kyc-legal-entity/signer-name-1]
      [:kyc-legal-entity/signer-email-1]
      [:kyc-legal-entity/type-of-identification-attachment-1]
      [:kyc-legal-entity/identification-attachment-1]
      [:kyc-legal-entity/signer-name-2]
      [:kyc-legal-entity/signer-email-2]
      [:kyc-legal-entity/type-of-identification-attachment-2]
      [:kyc-legal-entity/identification-attachment-2]])}

   :kyc-legal-entity/signature-info
   {:ord
    14.0
    :translations
    {:en ""
     :fi ""}

    :end-of-pdf?
    true

    :page-break-before?
    true

    :conditional-block
    (fn [_answers kyc-process]
      (some #{(:kyc-process/status kyc-process)}
            [:kyc-process.status/signatures
             :kyc-process.status/waiting-for-e-sign]))

    :questions
    (questions-registry/resolve-questions
     [[:kyc-legal-entity/signature-info-1]
      [:kyc-legal-entity/signature-info-2]])}

   :kyc-legal-entity/signature
   {:ord
    15.0

    :translations
    {:en "Signature"
     :fi "Allekirjoitus"}

    :end-of-pdf?
    true

    :conditional-block
    (fn [answers kyc-process]
      (and (some #{(:kyc-process/status kyc-process)}
                 [:kyc-process.status/signatures
                  :kyc-process.status/waiting-for-e-sign])
           (some #{(:kyc-legal-entity/number-of-signers answers)} [:one :two])))

    :section-not-read-only
    (fn [_answers kyc-process]
      (and (some #{(:kyc-process/status kyc-process)}
                 [:kyc-process.status/signatures
                  :kyc-process.status/waiting-for-e-sign])))

    :questions
    (questions-registry/resolve-questions
     [[:kyc-legal-entity/signature-place]
      [:kyc-legal-entity/signature-date]
      [:kyc-legal-entity/signature-name]
      [:kyc-legal-entity/signature-signature]])}

   :kyc-legal-entity/signature-2
   {:ord
    16.0

    :translations
    {:en "Signature 2"
     :fi "Allekirjoitus 2"}

    :end-of-pdf?
    true

    :conditional-block
    (fn [answers kyc-process]
      (and (some #{(:kyc-process/status kyc-process)}
                 [:kyc-process.status/signatures
                  :kyc-process.status/waiting-for-e-sign])
           (some #{(:kyc-legal-entity/number-of-signers answers)} [:two])))

    :section-not-read-only
    (fn [_answers kyc-process]
      (some #{(:kyc-process/status kyc-process)}
            [:kyc-process.status/signatures
             :kyc-process.status/waiting-for-e-sign]))

    :questions
    (questions-registry/resolve-questions
     [[:kyc-legal-entity/signature-place-2]
      [:kyc-legal-entity/signature-date-2]
      [:kyc-legal-entity/signature-name-2]
      [:kyc-legal-entity/signature-signature-2]])}})
